import { rawIfc } from './rawIfc'
import { decodePlacement, accumulateChain } from './ifcRawParse'

// ---------------------------------------------------------------------------
// Origens EXATAS da hierarquia espacial, lidas do próprio ficheiro IFC.
//
// Para rigor máximo de posicionamento não usamos nada derivado do
// Fragments (que recentra a geometria): cada origem é a composição exata
// da cadeia IfcLocalPlacement (folha→raiz) do STEP, via o parser paralelo
// web-ifc, convertida para METROS com o IfcUnitAssignment do projeto.
//
// Convenção de eixos: coordenadas do FICHEIRO IFC (x=E, y=N, z=h).
// A conversão para o mundo three (recentramento do conversor + geo
// placement) é responsabilidade do consumidor (ex.: originMarker).
// ---------------------------------------------------------------------------

export interface SpatialOrigin {
  expressID: number
  /** Categoria IFC (IFCSITE, IFCBUILDING, IFCBUILDINGSTOREY, IFCSPACE). */
  ifcType: string
  name: string | null
  /** Origem ABSOLUTA no referencial do ficheiro, em metros (E, N, h). */
  origin: [number, number, number]
  /** Origem relativa ao placement pai, em metros (E, N, h); null quando
   *  o placement não declara Location. */
  relative: [number, number, number] | null
  /** Profundidade indicativa para indentação na UI. */
  level: number
  /** A que referencial estão as coordenadas relativas: rótulo legível
   *  do dono do placement pai (ex.: 'IfcBuilding · Edifício X'), ou
   *  'origem do ficheiro' quando o placement é raiz. */
  parentLabel: string
}

export interface SpatialOriginsResult {
  unitLabel: string
  factorToMetres: number
  items: SpatialOrigin[]
}

const SPATIAL_TYPES: Array<{ type: string; level: number }> = [
  { type: 'IFCSITE', level: 0 },
  { type: 'IFCBUILDING', level: 1 },
  { type: 'IFCBUILDINGSTOREY', level: 2 },
  { type: 'IFCSPACE', level: 3 },
]

const TYPE_LABELS: Record<string, string> = {
  IFCSITE: 'IfcSite',
  IFCBUILDING: 'IfcBuilding',
  IFCBUILDINGSTOREY: 'IfcBuildingStorey',
  IFCSPACE: 'IfcSpace',
}

/** Nome legível da categoria (IfcBuildingStorey em vez de IFCBUILDINGSTOREY). */
export function spatialTypeLabel(ifcType: string): string {
  return TYPE_LABELS[ifcType] ?? ifcType
}

/** True quando o parser paralelo já tem este modelo carregado (a
 *  extração é então imediata, sem re-parse). */
export function spatialOriginsReady(modelId: string): boolean {
  return rawIfc.hasModel(modelId)
}

/** Extrai as origens exatas da hierarquia espacial. Carrega o parser
 *  paralelo on-demand (os bytes já estão em cache desde o load); em
 *  modelos grandes a primeira chamada custa segundos e memória. */
export async function extractSpatialOrigins(
  modelId: string,
): Promise<SpatialOriginsResult | null> {
  if (!rawIfc.hasModel(modelId)) {
    await rawIfc.loadModel(modelId)
  }
  if (!rawIfc.hasModel(modelId)) return null
  const unit = rawIfc.getLengthUnit(modelId)
  const factor = unit?.factorToMetres ?? 1
  const items: SpatialOrigin[] = []
  // Para dizer "em referência a quê": mapear o expressID do placement
  // de cada nó espacial para o seu rótulo, e ligar cada nó ao dono do
  // placement pai (chain[1]).
  const ownerByPlacement = new Map<number, string>()
  const parentPlacementOf = new Map<number, number | null>()

  for (const { type, level } of SPATIAL_TYPES) {
    let ids: number[]
    try {
      ids = rawIfc.getLineIDsByType(modelId, type)
    } catch {
      continue
    }
    for (const expressID of ids) {
      let name: string | null = null
      try {
        name = rawIfc.getAttributes(modelId, expressID)?.name ?? null
      } catch {
        /* sem nome */
      }
      let chainRaw: unknown[]
      try {
        chainRaw = rawIfc.getPlacementChain(modelId, expressID)
      } catch {
        chainRaw = []
      }
      const chain = chainRaw
        .map((p) => decodePlacement(p as Parameters<typeof decodePlacement>[0]))
        .filter((p): p is NonNullable<typeof p> => p !== null)
      if (chain.length === 0) {
        // Nó sem ObjectPlacement (raro): origem indefinida, ignora.
        continue
      }
      const acc = accumulateChain(chain)
      const abs = acc[0]
      const rel = chain[0].location
      const label = `${spatialTypeLabel(type)}${name ? ' · ' + name : ''}`
      if (chain[0].expressID !== null) {
        ownerByPlacement.set(chain[0].expressID, label)
      }
      parentPlacementOf.set(expressID, chain[1]?.expressID ?? null)
      items.push({
        expressID,
        ifcType: type,
        name,
        origin: [abs.x * factor, abs.y * factor, abs.z * factor],
        relative: rel
          ? [rel[0] * factor, rel[1] * factor, rel[2] * factor]
          : null,
        level,
        parentLabel: 'origem do ficheiro', // preenchido a seguir
      })
    }
  }
  for (const item of items) {
    const parentPl = parentPlacementOf.get(item.expressID) ?? null
    if (parentPl === null) {
      item.parentLabel = 'origem do ficheiro'
    } else {
      item.parentLabel =
        ownerByPlacement.get(parentPl) ?? `placement #${parentPl}`
    }
  }
  return {
    unitLabel: unit?.label ?? '(unidade desconhecida)',
    factorToMetres: factor,
    items,
  }
}
